import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from '@components/atoms/Popup';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Form } from '@components/atoms/Form';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { TranslationTabs } from '@components/molecules/TranslationTabs';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useUpdateStore } from '@services/useUpdateStore';
import { useGetMyAccountStoreDetail } from '@services/useGetMyAccountStoreDetail';
import { InputErrors, Translations } from '@types';
import styles from '../../MyCompany.module.css';
import { updateTranslation } from '@utils/translation';
import { REGEX_IS_EMAIL } from '@constants';

interface EditCompanyAddressContactsAndScheduleFormErrors {
    // address
    address: InputErrors;
    postalCode: InputErrors;
    city: InputErrors;
    country: InputErrors;
    latitude: InputErrors;
    longitude: InputErrors;
    // contacts
    phone: InputErrors;
    email: InputErrors;
    // schedule
    header1: InputErrors;
    content1: InputErrors;
    header2: InputErrors;
    content2: InputErrors;
}

interface EditCompanyAddressContactsAndScheduleModalProps {
    show: boolean;
    onClose: () => void;
}

export const EditCompanyAddressContactsAndScheduleModal: FC<EditCompanyAddressContactsAndScheduleModalProps> = ({
    show,
    onClose,
}) => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestUpdateStore,
        errorMessage,
        validationErrors,
        success: successMessage,
        lastResponseTime,
    } = useUpdateStore();
    const { request: requestMyAccountStoreDetail } = useGetMyAccountStoreDetail();
    const { myAccountStoreDetail, allLanguages, allSettings } = useData();
    const { t } = useTranslation();

    const automaticTranslations = allSettings?.data?.module_automatic_translations?.active;
    const languagesToTranslate = allLanguages?.data?.filter((item) => item.default !== 1) || [];

    const translations = {
        title: t('edit-company-modal-component.title'),
        addressLabel: t('edit-company-modal-component.address.label'),
        addressRequiredError: t('edit-company-modal-component.address.required-error'),
        postalCodeLabel: t('edit-company-modal-component.postal-code.label'),
        postalCodeRequiredError: t('edit-company-modal-component.postal-code.required-error'),
        cityLabel: t('edit-company-modal-component.city.label'),
        cityRequiredError: t('edit-company-modal-component.city.required-error'),
        countryLabel: t('edit-company-modal-component.country.label'),
        countryRequiredError: t('edit-company-modal-component.country.required-error'),
        latitudeLabel: t('edit-company-modal-component.latitude.label'),
        longitudeLabel: t('edit-company-modal-component.longitude.label'),
        phoneLabel: t('edit-company-modal-component.phone.label'),
        emailLabel: t('edit-company-modal-component.email.label'),
        emailInvalidError: t('edit-company-modal-component.email.invalid-error'),
        header1Label: t('edit-company-modal-component.schedule.header-1.label'),
        content1Label: t('edit-company-modal-component.schedule.content-1.label'),
        header2Label: t('edit-company-modal-component.schedule.header-2.label'),
        content2Label: t('edit-company-modal-component.schedule.content-2.label'),
        saveButton: t('edit-company-modal-component.save.button'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    // address
    const [address, setAddress] = useState<string>('');
    const [postalCode, setPostalCode] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [country, setCountry] = useState<string>('');
    const [latitude, setLatitude] = useState<number | null>(null);
    const [longitude, setLongitude] = useState<number | null>(null);
    // contacts
    const [phone, setPhone] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    // schedule
    const [header1, setHeader1] = useState<string>('');
    const [content1, setContent1] = useState<string>('');
    const [header2, setHeader2] = useState<string>('');
    const [content2, setContent2] = useState<string>('');
    const [header1Translations, setHeader1Translations] = useState<Translations>([]);
    const [content1Translations, setContent1Translations] = useState<Translations>([]);
    const [header2Translations, setHeader2Translations] = useState<Translations>([]);
    const [content2Translations, setContent2Translations] = useState<Translations>([]);

    // when modal opens, update values for modal
    useEffect(() => {
        if (show) {
            // address
            setAddress(myAccountStoreDetail?.data?.address?.street || '');
            setPostalCode(myAccountStoreDetail?.data?.address?.postal_code || '');
            setCity(myAccountStoreDetail?.data?.address?.city || '');
            setCountry(myAccountStoreDetail?.data?.address?.country || '');
            setLatitude(myAccountStoreDetail?.data?.address?.latitude || null);
            setLongitude(myAccountStoreDetail?.data?.address?.longitude || null);
            // contacts
            setPhone(myAccountStoreDetail?.data?.mobile_phone_number || '');
            setEmail(myAccountStoreDetail?.data?.email || '');
            // schedule
            setHeader1(myAccountStoreDetail?.data?.schedule?.schedule_header_1 || '');
            setContent1(myAccountStoreDetail?.data?.schedule?.schedule_text_1 || '');
            setHeader2(myAccountStoreDetail?.data?.schedule?.schedule_header_2 || '');
            setContent2(myAccountStoreDetail?.data?.schedule?.schedule_text_2 || '');
            setHeader1Translations(
                myAccountStoreDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'schedule_header_1')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
            setContent1Translations(
                myAccountStoreDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'schedule_text_1')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
            setHeader2Translations(
                myAccountStoreDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'schedule_header_2')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
            setContent2Translations(
                myAccountStoreDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'schedule_text_2')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        if (!initialLoadDone && address && city && country) {
            setInitialLoadDone(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address, city, country]);

    useEffect(() => {
        if (initialLoadDone) {
            setLatitude(null);
            setLongitude(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address, city, country]);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<EditCompanyAddressContactsAndScheduleFormErrors>({
        // address
        address: [],
        postalCode: [],
        city: [],
        country: [],
        latitude: [],
        longitude: [],
        // contacts
        phone: [],
        email: [],
        // schedule
        header1: [],
        content1: [],
        header2: [],
        content2: [],
    });

    const handleOnClickSave = () => {
        const errors = validateForm();

        if (
            // address
            errors.address.length === 0 &&
            errors.postalCode.length === 0 &&
            errors.city.length === 0 &&
            errors.country.length === 0 &&
            errors.latitude.length === 0 &&
            errors.longitude.length === 0 &&
            // contacts
            errors.phone.length === 0 &&
            errors.email.length === 0 &&
            // schedule
            errors.header1.length === 0 &&
            errors.content1.length === 0 &&
            errors.header2.length === 0 &&
            errors.content2.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => EditCompanyAddressContactsAndScheduleFormErrors = () => {
        const newErrors: EditCompanyAddressContactsAndScheduleFormErrors = {
            // address
            address: [],
            postalCode: [],
            city: [],
            country: [],
            latitude: [],
            longitude: [],
            // contacts
            phone: [],
            email: [],
            // schedule
            header1: [],
            content1: [],
            header2: [],
            content2: [],
        };

        // check if email is valid
        email.trim() && !REGEX_IS_EMAIL.test(email.trim()) && newErrors.email.push(translations.emailInvalidError);
        !address.trim() && newErrors.address.push(translations.addressRequiredError);
        !postalCode.trim() && newErrors.postalCode.push(translations.postalCodeRequiredError);
        !city.trim() && newErrors.city.push(translations.cityRequiredError);
        !country.trim() && newErrors.country.push(translations.countryRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inputTranslations: any = {};

        header1Translations.forEach((item) => (inputTranslations[`schedule_header_1_${item.languageCode}`] = item.translation));
        content1Translations.forEach((item) => (inputTranslations[`schedule_text_1_${item.languageCode}`] = item.translation));
        header2Translations.forEach((item) => (inputTranslations[`schedule_header_2_${item.languageCode}`] = item.translation));
        content2Translations.forEach((item) => (inputTranslations[`schedule_text_2_${item.languageCode}`] = item.translation));

        requestUpdateStore(
            { uuid: myAccountStoreDetail?.data?.uuid || '' },
            {
                // address
                address: address,
                postal_code: postalCode,
                city: city,
                country: country,
                latitude: latitude,
                longitude: longitude,
                // contacts
                mobile_phone_number: phone,
                email: email,
                // schedule
                schedule_header_1: header1,
                schedule_text_1: content1,
                schedule_header_2: header2,
                schedule_text_2: content2,
                ...inputTranslations,
            }
        );
    };

    // if edit service returned successfully, dispatch notification and update
    // if not, show error
    useEffect(() => {
        if (lastResponseTime) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: errorMessage || '' });
            } else if (validationErrors) {
                setErrors({
                    // match form request errors with frontend errors object
                    address: validationErrors?.address || [],
                    postalCode: validationErrors?.postal_code || [],
                    city: validationErrors?.city || [],
                    country: validationErrors?.country || [],
                    latitude: validationErrors?.latitude || [],
                    longitude: validationErrors?.longitude || [],
                    phone: validationErrors?.mobile_phone_number || [],
                    email: validationErrors?.email || [],
                    header1: validationErrors?.schedule_header_1 || [],
                    content1: validationErrors?.schedule_text_1 || [],
                    header2: validationErrors?.schedule_header_2 || [],
                    content2: validationErrors?.schedule_text_2 || [],
                });
            } else {
                showDefaultNotification({ type: 'success', message: successMessage || '' });
                // update form fields
                onClose();
                requestMyAccountStoreDetail({ uuid: myAccountStoreDetail?.data?.uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Popup
            show={show}
            onHide={() => onClose()}
            desktopWidth="1220px"
            fullWidthOnMobile={true}
            header={
                <>
                    <div className={styles.header}>
                        <Typography tagVariant={TypographyTagVariants.h5} fontWeight="900" fontSize="20">
                            {translations.title}
                        </Typography>
                    </div>
                    <hr className={styles.divider} />
                </>
            }>
            <div className={styles['content-container']}>
                <Form formRef={formRef} onSubmit={submitForm}>
                    <div className="d-flex flex-column gap-3">
                        {/* address */}
                        <Input
                            type={InputTypes.text}
                            label={`${translations.addressLabel}*`}
                            variant={InputVariants.labeledInput}
                            value={address}
                            onChange={(value) => setAddress(value)}
                        />

                        <div className="row">
                            <div className="col-md-4 col-12">
                                <Input
                                    type={InputTypes.text}
                                    label={`${translations.postalCodeLabel}*`}
                                    variant={InputVariants.labeledInput}
                                    value={postalCode}
                                    onChange={(value) => setPostalCode(value)}
                                />
                            </div>
                            <div className="col-md-4 col-12">
                                <Input
                                    type={InputTypes.text}
                                    label={`${translations.cityLabel}*`}
                                    variant={InputVariants.labeledInput}
                                    value={city}
                                    onChange={(value) => setCity(value)}
                                />
                            </div>
                            <div className="col-md-4 col-12">
                                <Input
                                    type={InputTypes.text}
                                    label={`${translations.countryLabel}*`}
                                    variant={InputVariants.labeledInput}
                                    value={country}
                                    onChange={(value) => setCountry(value)}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-4 col-12">
                                <Input
                                    type={InputTypes.number}
                                    label={translations.latitudeLabel}
                                    variant={InputVariants.labeledInput}
                                    value={latitude !== null ? String(latitude) : ''}
                                    onChange={(value) => setLatitude(value)}
                                />
                            </div>
                            <div className="col-md-4 col-12">
                                <Input
                                    type={InputTypes.number}
                                    label={translations.longitudeLabel}
                                    variant={InputVariants.labeledInput}
                                    value={longitude !== null ? String(longitude) : ''}
                                    onChange={(value) => setLongitude(value)}
                                />
                            </div>
                        </div>

                        <hr />

                        {/* contacts */}
                        <div className="row">
                            <div className="col-md-6 col-12">
                                <Input
                                    type={InputTypes.text}
                                    label={translations.phoneLabel}
                                    variant={InputVariants.labeledInput}
                                    value={phone}
                                    onChange={(value) => setPhone(value)}
                                />
                            </div>
                            <div className="col-md-6 col-12">
                                <Input
                                    type={InputTypes.email}
                                    label={translations.emailLabel}
                                    variant={InputVariants.labeledInput}
                                    value={email}
                                    onChange={(value) => setEmail(value)}
                                    state={errors.email.length > 0 ? InputStates.error : undefined}
                                    errorMessages={errors.email}
                                />
                            </div>
                        </div>

                        <hr />

                        {/* schedule */}
                        <div className="row">
                            <div className="col-md-6 col-12">
                                <div className="d-flex flex-column gap-3">
                                    <Input
                                        type={InputTypes.text}
                                        label={translations.header1Label}
                                        variant={InputVariants.labeledInput}
                                        value={header1}
                                        onChange={(value) => setHeader1(value)}
                                    />
                                    <Input
                                        type={InputTypes.text}
                                        label={translations.content1Label}
                                        variant={InputVariants.labeledInput}
                                        value={content1}
                                        onChange={(value) => setContent1(value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="d-flex flex-column gap-3">
                                    <Input
                                        type={InputTypes.text}
                                        label={translations.header2Label}
                                        variant={InputVariants.labeledInput}
                                        value={header2}
                                        onChange={(value) => setHeader2(value)}
                                    />
                                    <Input
                                        type={InputTypes.text}
                                        label={translations.content2Label}
                                        variant={InputVariants.labeledInput}
                                        value={content2}
                                        onChange={(value) => setContent2(value)}
                                    />
                                </div>
                            </div>

                            {!automaticTranslations && ( <TranslationTabs
                                contentByLanguage={languagesToTranslate.map((language) => {
                                    return {
                                        language: language.language_code || '',
                                        content: (
                                            <div className="row">
                                                <div className="col-md-6 col-12">
                                                    <div className="d-flex flex-column gap-3">
                                                        <Input
                                                            type={InputTypes.text}
                                                            label={translations.header1Label}
                                                            variant={InputVariants.labeledInput}
                                                            value={
                                                                header1Translations.find(
                                                                    (item) => item.languageCode === language.language_code
                                                                )?.translation || ''
                                                            }
                                                            onChange={(value) =>
                                                                setHeader1Translations((previous) =>
                                                                    updateTranslation(
                                                                        previous,
                                                                        language.language_code || '',
                                                                        value
                                                                    )
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            type={InputTypes.text}
                                                            label={translations.content1Label}
                                                            variant={InputVariants.labeledInput}
                                                            value={
                                                                content1Translations.find(
                                                                    (item) => item.languageCode === language.language_code
                                                                )?.translation || ''
                                                            }
                                                            onChange={(value) =>
                                                                setContent1Translations((previous) =>
                                                                    updateTranslation(
                                                                        previous,
                                                                        language.language_code || '',
                                                                        value
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-12">
                                                    <div className="d-flex flex-column gap-3">
                                                        <Input
                                                            type={InputTypes.text}
                                                            label={translations.header2Label}
                                                            variant={InputVariants.labeledInput}
                                                            value={
                                                                header2Translations.find(
                                                                    (item) => item.languageCode === language.language_code
                                                                )?.translation || ''
                                                            }
                                                            onChange={(value) =>
                                                                setHeader2Translations((previous) =>
                                                                    updateTranslation(
                                                                        previous,
                                                                        language.language_code || '',
                                                                        value
                                                                    )
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            type={InputTypes.text}
                                                            label={translations.content2Label}
                                                            variant={InputVariants.labeledInput}
                                                            value={
                                                                content2Translations.find(
                                                                    (item) => item.languageCode === language.language_code
                                                                )?.translation || ''
                                                            }
                                                            onChange={(value) =>
                                                                setContent2Translations((previous) =>
                                                                    updateTranslation(
                                                                        previous,
                                                                        language.language_code || '',
                                                                        value
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                    };
                                })}
                            />)}
                        </div>

                        <Button
                            label={translations.saveButton}
                            classes="w-100"
                            mode={ButtonMode.primary}
                            onClick={handleOnClickSave}
                        />
                    </div>
                </Form>
            </div>
        </Popup>
    );
};
